import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import { businessDateOf, type CargosProblemCode } from '@rental/domain'
import { countryOptions, format, formatDate, formatDateTime } from '@rental/i18n'
import { Button, Checkbox, cn, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { ChoiceChips } from '../components/ChoiceChips'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { Section } from '../components/Section'
import { ReasonList } from '../components/SubPanel'
import { Facts, FIELD, FIELD_ROW, ProblemNotice, StaffInput, StaffSelect } from '../components/ui'
import { useLocale } from '../i18n'
import { compactPhone, E164, type IdentityDraft, identityReasons } from './identity-readiness'
import { type Place, PlacePicker, placeName } from './PlacePicker'
import type { DraftPatch } from './scan/review'
import { ScanDialog } from './scan/ScanDialog'
import type { LicenceRule } from './scan/scan-config'

type Sheet = ApiSchemas['CheckoutSheet']
type Identity = ApiSchemas['StaffIdentity']
type Prefill = ApiSchemas['IdentityPrefill']

type FormField =
  | 'surname'
  | 'givenNames'
  | 'birthDate'
  | 'birthPlace'
  | 'citizenship'
  | 'address'
  | 'residencePlace'
  | 'documentType'
  | 'documentNumber'
  | 'documentPlace'
  | 'licenceNumber'
  | 'licencePlace'
  | 'phone'

/** Where a problem with CaRGOS's record shows on the form. */
const FIELD_OF: Readonly<Record<string, FormField>> = {
  CONDUCENTE_CONTRAENTE_COGNOME: 'surname',
  CONDUCENTE_CONTRAENTE_NOME: 'givenNames',
  CONDUCENTE_CONTRAENTE_NASCITA_DATA: 'birthDate',
  CONDUCENTE_CONTRAENTE_NASCITA_LUOGO_COD: 'birthPlace',
  CONDUCENTE_CONTRAENTE_CITTADINANZA_COD: 'citizenship',
  CONDUCENTE_CONTRAENTE_RESIDENZA_LUOGO_COD: 'residencePlace',
  CONDUCENTE_CONTRAENTE_RESIDENZA_INDIRIZZO: 'address',
  CONDUCENTE_CONTRAENTE_DOCIDE_TIPO_COD: 'documentType',
  CONDUCENTE_CONTRAENTE_DOCIDE_NUMERO: 'documentNumber',
  CONDUCENTE_CONTRAENTE_DOCIDE_LUOGORIL_COD: 'documentPlace',
  CONDUCENTE_CONTRAENTE_PATENTE_NUMERO: 'licenceNumber',
  CONDUCENTE_CONTRAENTE_PATENTE_LUOGORIL_COD: 'licencePlace',
  CONDUCENTE_CONTRAENTE_RECAPITO: 'phone',
  // Q-162: the second driver is CaRGOS's CONDUCENTE2, which has no residence: nor has their form.
  CONDUCENTE2_COGNOME: 'surname',
  CONDUCENTE2_NOME: 'givenNames',
  CONDUCENTE2_NASCITA_DATA: 'birthDate',
  CONDUCENTE2_NASCITA_LUOGO_COD: 'birthPlace',
  CONDUCENTE2_CITTADINANZA_COD: 'citizenship',
  CONDUCENTE2_DOCIDE_TIPO_COD: 'documentType',
  CONDUCENTE2_DOCIDE_NUMERO: 'documentNumber',
  CONDUCENTE2_DOCIDE_LUOGORIL_COD: 'documentPlace',
  CONDUCENTE2_PATENTE_NUMERO: 'licenceNumber',
  CONDUCENTE2_PATENTE_LUOGORIL_COD: 'licencePlace',
  CONDUCENTE2_RECAPITO: 'phone',
}

/** Where the focus lands when the form opens: its first field. */
const FIRST_FIELD = 'input:not([type="hidden"]), select, textarea'

type Draft = IdentityDraft

/** The form's start: what the server prefilled. Never the ID number (decision 2). */
function draftOf(prefill: Prefill): Draft {
  return {
    surname: prefill.surname ?? '',
    givenNames: prefill.givenNames ?? '',
    birthDate: prefill.birthDate ?? '',
    birthPlace: prefill.birthPlace,
    citizenship: prefill.citizenship,
    address: prefill.residence.address ?? '',
    postalCode: prefill.residence.postalCode ?? '',
    noPostalCode: prefill.residence.noPostalCode,
    city: prefill.residence.city ?? '',
    country: prefill.residence.country ?? '',
    residencePlace: prefill.residence.place,
    addressSource: prefill.addressSource ?? '',
    documentType: prefill.documentType ?? '',
    documentNumber: '',
    documentPlace: prefill.documentPlace,
    licenceNumber: prefill.licenceNumber ?? '',
    licencePlace: prefill.licencePlace,
    phone: prefill.phone ?? '',
  }
}

/**
 * Decision 5 (S6): who takes the vehicle, as the counter reads it off the
 * original documents, before every hand-over. Where CaRGOS applies, also its
 * details: the ID document, the licence, the places. First on the screen, so
 * the home address is on file when the iPad turns for the contract.
 *
 * Q-162: with `driver="second"`, the same for the booking's second driver, but
 * for the home address: CaRGOS's CONDUCENTE2 has none, nor has their form. Their
 * phone is required where CaRGOS applies (CONDUCENTE2_RECAPITO). Nothing when
 * the booking has no second driver.
 */
export function IdentitySection({
  sheet,
  driver = 'main',
}: {
  sheet: Sheet
  /** Whose identity: the renter's, or the second driver's (Q-162). */
  driver?: 'main' | 'second'
}) {
  const person = driver === 'second' ? sheet.secondIdentity : sheet.identity
  return person ? <IdentityForm sheet={sheet} driver={driver} person={person} /> : null
}

type Person = Pick<Sheet['identity'], 'latest' | 'prefill' | 'bookingBirthDate'> & {
  firstName?: string
  lastName?: string
}

function IdentityForm({
  sheet,
  driver,
  person,
}: {
  sheet: Sheet
  driver: 'main' | 'second'
  person: Person
}) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const cargos = sheet.identity.form === 'cargos'
  const second = driver === 'second'
  const latest = person.latest
  const [editing, setEditing] = useState(latest === null)
  const [draft, setDraft] = useState(() => draftOf(person.prefill))
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [fieldProblems, setFieldProblems] = useState<Partial<Record<FormField, string>>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  // The scanner needs the server flag; a counter without a camera chooses a photo (OPS-1).
  const canScan = sheet.identity.scanEnabled
  // The booked category's licence rule, for the scan's warn-only category check: the
  // configuration's own list (CLAUDE.md), the one the server applies at hand-over.
  const options = useQuery({
    queryKey: ['category-options', sheet.category.id],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/categories/{categoryId}/options', {
            params: { path: { categoryId: sheet.category.id } },
          }),
        ),
      ),
    enabled: canScan,
    staleTime: 60_000,
  })
  const licence: LicenceRule | undefined =
    options.data?.eligibility == null ? undefined : options.data.eligibility.licence
  // A confirmed scan fills the plain text fields; places and the ID number are
  // left for the operator (Decision 2), and every field stays editable.
  const applyScan = (patch: DraftPatch) => setDraft((current) => ({ ...current, ...patch }))
  const form = useRef<HTMLDivElement>(null)
  const editArea = useRef<HTMLDivElement>(null)
  const today = businessDateOf(new Date())
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))
  const countries = countryOptions(locale)

  const phone = compactPhone(draft.phone)
  const phoneRequired = second && cargos
  const reasons = identityReasons(draft, cargos, driver)
  const complete = reasons.length === 0
  const reasonsId = useId()

  async function save() {
    // Q-162: a second driver's form asks no home address, and so no source for one.
    const home = second || draft.addressSource === '' ? null : draft.addressSource
    if (!complete || (!second && home === null)) return
    setProblem(null)
    setFieldProblems({})
    setNotice(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/identity', {
        params: { path: { code: sheet.code } },
        body: {
          surname: draft.surname.trim(),
          givenNames: draft.givenNames.trim(),
          birthDate: draft.birthDate,
          ...(home
            ? {
                residence: {
                  address: draft.address.trim(),
                  ...(draft.noPostalCode
                    ? { noPostalCode: true as const }
                    : { postalCode: draft.postalCode.trim() }),
                  city: draft.city.trim(),
                  country: draft.country,
                },
                addressSource: home,
              }
            : {}),
          ...(phone ? { phone } : {}),
          ...(second ? { role: 'second' as const } : {}),
          ...(cargos &&
          draft.birthPlace &&
          draft.citizenship &&
          (second || draft.residencePlace) &&
          draft.documentPlace &&
          draft.licencePlace
            ? {
                cargos: {
                  birthPlaceCode: draft.birthPlace.code,
                  citizenshipCode: draft.citizenship.code,
                  ...(!second && draft.residencePlace
                    ? { residencePlaceCode: draft.residencePlace.code }
                    : {}),
                  document: {
                    type: draft.documentType,
                    number: draft.documentNumber.trim(),
                    placeCode: draft.documentPlace.code,
                  },
                  licence: {
                    number: draft.licenceNumber.trim(),
                    placeCode: draft.licencePlace.code,
                  },
                },
              }
            : {}),
        },
      }),
    )
    setBusy(false)
    if (result.ok) {
      setNotice(result.data.recorded ? m.identity.saved : m.identity.unchanged)
      setEditing(false)
      // The number is typed again for any later correction.
      set('documentNumber', '')
      await queryClient.invalidateQueries({
        queryKey: ['checkout', sheet.code],
      })
      return
    }
    if (takeOver(result.problem)) return
    const problems = result.problem.kind === 'api' ? result.problem.details.problems : undefined
    if (result.problem.kind === 'api' && result.problem.code === 'cargos_record_invalid') {
      const found: Partial<Record<FormField, string>> = {}
      for (const item of Array.isArray(problems) ? problems : []) {
        const field = FIELD_OF[String(item?.field)]
        const code = String(item?.code) as CargosProblemCode
        if (field && m.identity.problem[code]) found[field] = m.identity.problem[code]
      }
      setFieldProblems(found)
    }
    setProblem(result.problem)
  }

  const receiptCovers = latest
    ? sheet.cargos.transmissions.some(
        (transmission) =>
          (transmission.status === 'sent' || transmission.status === 'recorded') &&
          (second ? transmission.secondIdentityId : transmission.identityId) === latest.id,
      )
    : false

  return (
    <Section
      title={
        second
          ? format(m.identity.secondTitle, {
              name: [person.firstName, person.lastName].filter(Boolean).join(' '),
            })
          : m.identity.title
      }
    >
      <p className="text-sm text-text-muted">
        {second
          ? cargos
            ? m.identity.secondIntroCargos
            : m.identity.secondIntro
          : cargos
            ? m.identity.introCargos
            : m.identity.intro}
      </p>
      {/* Saved, the form closes under the button pressed: what was recorded takes the focus. */}
      {notice ? (
        <OutcomeNotice key={notice}>
          <p>{notice}</p>
        </OutcomeNotice>
      ) : null}
      {latest && !editing ? <RecordedIdentity identity={latest} sheet={sheet} /> : null}
      {latest && !editing ? (
        <div className="flex flex-col gap-2">
          {receiptCovers ? <p className="text-sm">{m.identity.editAfterReceipt}</p> : null}
          <div ref={editArea}>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => {
                // The button goes with the record it edits: the focus goes to the first field.
                flushSync(() => {
                  setNotice(null)
                  setDraft(draftOf(person.prefill))
                  setEditing(true)
                })
                form.current?.querySelector<HTMLElement>(FIRST_FIELD)?.focus()
              }}
            >
              {m.identity.edit}
            </Button>
          </div>
        </div>
      ) : null}
      {editing ? (
        <div ref={form} className="flex flex-col gap-6">
          {canScan ? (
            <div>
              <Button variant="outline" onClick={() => setScanning(true)}>
                {m.scan.button}
              </Button>
            </div>
          ) : null}
          {scanning ? (
            <ScanDialog
              onClose={() => setScanning(false)}
              onApply={applyScan}
              current={{
                surname: draft.surname,
                givenNames: draft.givenNames,
                birthDate: draft.birthDate,
                documentType: draft.documentType,
                licenceNumber: draft.licenceNumber,
              }}
              // The licence must still be valid on the day the vehicle comes back.
              returnDate={businessDateOf(new Date(sheet.endsAt))}
              licence={licence}
            />
          ) : null}
          <div className={FIELD_ROW}>
            <Field
              className={FIELD}
              label={m.identity.surname}
              hint={m.identity.namesHint}
              error={fieldProblems.surname}
              required
            >
              {(control) => (
                <StaffInput
                  {...control}
                  autoComplete="off"
                  maxLength={100}
                  value={draft.surname}
                  onChange={(event) => set('surname', event.target.value)}
                />
              )}
            </Field>
            <Field
              className={FIELD}
              label={m.identity.givenNames}
              error={fieldProblems.givenNames}
              required
            >
              {(control) => (
                <StaffInput
                  {...control}
                  autoComplete="off"
                  maxLength={100}
                  value={draft.givenNames}
                  onChange={(event) => set('givenNames', event.target.value)}
                />
              )}
            </Field>
            <Field
              className={FIELD}
              label={m.identity.birthDate}
              error={fieldProblems.birthDate}
              hint={
                person.bookingBirthDate &&
                draft.birthDate !== '' &&
                draft.birthDate !== person.bookingBirthDate
                  ? format(
                      second ? m.identity.secondBirthDateDiffers : m.identity.birthDateDiffers,
                      {
                        date: formatDate(locale, person.bookingBirthDate),
                      },
                    )
                  : undefined
              }
              required
            >
              {(control) => (
                <StaffInput
                  {...control}
                  type="date"
                  max={today}
                  value={draft.birthDate}
                  onChange={(event) => set('birthDate', event.target.value)}
                />
              )}
            </Field>
            {cargos ? (
              <>
                <PlacePicker
                  className={FIELD}
                  label={m.identity.birthPlace}
                  kind="any"
                  value={draft.birthPlace}
                  onChange={(place) => set('birthPlace', place)}
                  error={fieldProblems.birthPlace}
                />
                <PlacePicker
                  className={FIELD}
                  label={m.identity.citizenship}
                  kind="state"
                  value={draft.citizenship}
                  onChange={(place) => set('citizenship', place)}
                  error={fieldProblems.citizenship}
                />
              </>
            ) : null}
          </div>

          {/* Q-162: the renter's home address; a second driver is asked none. */}
          {second ? null : (
            <fieldset className="flex flex-col gap-4">
              <legend className="mb-2 text-lg font-bold text-brand-strong">
                {m.identity.residence}
              </legend>
              <div className={FIELD_ROW}>
                <Field
                  className={FIELD}
                  label={m.identity.address}
                  error={fieldProblems.address}
                  required
                >
                  {(control) => (
                    <StaffInput
                      {...control}
                      autoComplete="off"
                      maxLength={200}
                      value={draft.address}
                      onChange={(event) => set('address', event.target.value)}
                    />
                  )}
                </Field>
                <div className={cn(FIELD, 'flex flex-col gap-1')}>
                  <Field label={m.identity.postalCode} required={!draft.noPostalCode}>
                    {(control) => (
                      <StaffInput
                        {...control}
                        autoComplete="off"
                        maxLength={12}
                        disabled={draft.noPostalCode}
                        value={draft.noPostalCode ? '' : draft.postalCode}
                        onChange={(event) => set('postalCode', event.target.value)}
                      />
                    )}
                  </Field>
                  <Checkbox
                    label={m.identity.noPostalCode}
                    checked={draft.noPostalCode}
                    onChange={(event) => set('noPostalCode', event.target.checked)}
                  />
                </div>
                <Field className={FIELD} label={m.identity.city} required>
                  {(control) => (
                    <StaffInput
                      {...control}
                      autoComplete="off"
                      maxLength={100}
                      value={draft.city}
                      onChange={(event) => set('city', event.target.value)}
                    />
                  )}
                </Field>
                <Field className={FIELD} label={m.identity.country} required>
                  {(control) => (
                    <StaffSelect
                      {...control}
                      value={draft.country}
                      onChange={(event) => set('country', event.target.value)}
                    >
                      <option value="" />
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </StaffSelect>
                  )}
                </Field>
                {cargos ? (
                  <PlacePicker
                    className={FIELD}
                    label={m.identity.residencePlace}
                    kind="any"
                    on={today}
                    value={draft.residencePlace}
                    onChange={(place) => set('residencePlace', place)}
                    error={fieldProblems.residencePlace}
                  />
                ) : null}
              </div>
              <ChoiceChips
                name={`identity-source-${sheet.code}`}
                label={m.identity.addressSource}
                options={(['document', 'declared'] as const).map((source) => ({
                  value: source,
                  label: m.identity.sources[source],
                }))}
                value={draft.addressSource}
                onChange={(source) => set('addressSource', source)}
              />
            </fieldset>
          )}

          {cargos ? (
            <>
              <fieldset className="flex flex-col gap-4">
                <legend className="mb-2 text-lg font-bold text-brand-strong">
                  {m.identity.document}
                </legend>
                <div className={FIELD_ROW}>
                  <Field
                    className={FIELD}
                    label={m.identity.documentType}
                    error={fieldProblems.documentType}
                    required
                  >
                    {(control) => (
                      <StaffSelect
                        {...control}
                        value={draft.documentType}
                        onChange={(event) => set('documentType', event.target.value)}
                      >
                        <option value="" />
                        {sheet.identity.documentTypes.map((type) => (
                          // CaRGOS's table, in Italian whatever the app's language (WCAG 3.1.2).
                          <option key={type.code} value={type.code} lang="it">
                            {type.description}
                          </option>
                        ))}
                      </StaffSelect>
                    )}
                  </Field>
                  <Field
                    className={FIELD}
                    label={m.identity.documentNumber}
                    hint={m.identity.documentNumberHint}
                    error={fieldProblems.documentNumber}
                    required
                  >
                    {(control) => (
                      <StaffInput
                        {...control}
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        maxLength={40}
                        value={draft.documentNumber}
                        onChange={(event) => set('documentNumber', event.target.value)}
                      />
                    )}
                  </Field>
                  <PlacePicker
                    className={FIELD}
                    label={m.identity.documentPlace}
                    kind="any"
                    value={draft.documentPlace}
                    onChange={(place) => set('documentPlace', place)}
                    error={fieldProblems.documentPlace}
                  />
                </div>
              </fieldset>
              <fieldset className="flex flex-col gap-4">
                <legend className="mb-2 text-lg font-bold text-brand-strong">
                  {m.identity.licence}
                </legend>
                <div className={FIELD_ROW}>
                  <Field
                    className={FIELD}
                    label={m.identity.licenceNumber}
                    error={fieldProblems.licenceNumber}
                    required
                  >
                    {(control) => (
                      <StaffInput
                        {...control}
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        maxLength={40}
                        value={draft.licenceNumber}
                        onChange={(event) => set('licenceNumber', event.target.value)}
                      />
                    )}
                  </Field>
                  <PlacePicker
                    className={FIELD}
                    label={m.identity.licencePlace}
                    kind="any"
                    value={draft.licencePlace}
                    onChange={(place) => set('licencePlace', place)}
                    error={fieldProblems.licencePlace}
                  />
                </div>
              </fieldset>
            </>
          ) : null}

          <div className={FIELD_ROW}>
            <Field
              className={FIELD}
              label={m.identity.phone}
              hint={phoneRequired ? m.identity.secondPhoneHint : m.identity.phoneHint}
              {...(phoneRequired ? { required: true } : { optionalLabel: m.newBooking.optional })}
              error={
                fieldProblems.phone ??
                (phone !== '' && !E164.test(phone) ? m.identity.phoneInvalid : undefined)
              }
            >
              {(control) => (
                <StaffInput
                  {...control}
                  type="tel"
                  autoComplete="off"
                  maxLength={20}
                  value={draft.phone}
                  onChange={(event) => set('phone', event.target.value)}
                />
              )}
            </Field>
          </div>

          {Object.keys(fieldProblems).length > 0 ? (
            <Notice tone="danger" live="assertive">
              <p>{m.identity.problems}</p>
            </Notice>
          ) : (
            <ProblemNotice problem={problem} />
          )}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="staff"
              busy={busy}
              disabled={!complete && !busy}
              aria-describedby={!complete && !busy ? reasonsId : undefined}
              onClick={() => void save()}
            >
              {m.identity.save}
            </Button>
            {latest ? (
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => {
                  // The form goes, Modifica is back: the focus goes to it, not to the page.
                  flushSync(() => setEditing(false))
                  editArea.current?.querySelector<HTMLElement>('button')?.focus()
                }}
              >
                {m.identity.cancel}
              </Button>
            ) : null}
          </div>
          <ReasonList
            id={reasonsId}
            reasons={busy ? [] : reasons.map((reason) => m.identity.why[reason])}
          />
        </div>
      ) : null}
    </Section>
  )
}

/** The identity on file, as recorded: the ID number until CaRGOS has the report. */
function RecordedIdentity({ identity, sheet }: { identity: Identity; sheet: Sheet }) {
  const { locale, messages: m } = useLocale()
  const place = (value: Place | null) => (value ? placeName(value, m.places.state) : '—')
  const residence = identity.residence
  const items: (readonly [string, string])[] = [
    [m.identity.surname, identity.surname],
    [m.identity.givenNames, identity.givenNames],
    [m.identity.birthDate, formatDate(locale, identity.birthDate)],
    ...(identity.cargos
      ? ([
          [m.identity.birthPlace, place(identity.birthPlace)],
          [m.identity.citizenship, place(identity.citizenship)],
        ] as const)
      : []),
    // Q-162: a second driver's identity has no home address.
    ...(residence
      ? ([
          [
            m.identity.residence,
            [
              residence.address,
              [residence.postalCode, residence.city].filter(Boolean).join(' '),
              residence.country,
            ]
              .filter(Boolean)
              .join(', '),
          ],
          ...(identity.cargos
            ? ([[m.identity.residencePlace, place(residence.place)]] as const)
            : []),
        ] as const)
      : []),
    ...(identity.addressSource
      ? ([[m.identity.addressSource, m.identity.sources[identity.addressSource]]] as const)
      : []),
    ...(identity.document
      ? ([
          [
            m.identity.document,
            [
              sheet.identity.documentTypes.find((type) => type.code === identity.document?.type)
                ?.description ?? identity.document.type,
              identity.document.number ??
                (identity.document.erasedAt
                  ? format(m.identity.erased, {
                      date: formatDateTime(locale, identity.document.erasedAt),
                    })
                  : '—'),
              identity.document.place ? place(identity.document.place) : null,
            ]
              .filter(Boolean)
              .join(' · '),
          ],
        ] as const)
      : []),
    ...(identity.licence
      ? ([
          [m.identity.licence, `${identity.licence.number} · ${place(identity.licence.place)}`],
        ] as const)
      : []),
    ...(identity.phone ? ([[m.identity.phone, identity.phone]] as const) : []),
  ]
  return (
    <div className="flex flex-col gap-3">
      <Facts items={items} />
      <p className="text-sm text-text-muted">
        {format(m.identity.recordedBy, {
          name: identity.capturedBy,
          date: formatDateTime(locale, identity.capturedAt),
        })}
      </p>
    </div>
  )
}
